'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { getConsent, setConsent, openConsentSettings, OPEN_CONSENT_EVENT } from '@/lib/consent'
import { startAnalytics } from '@/lib/analytics'

// Mounted once in the root layout: starts the tag loader and asks for cookie
// consent until the visitor has chosen. "Cookie settings" links reopen it.
export default function ConsentBanner() {
  const [open, setOpen] = useState(false)
  const [choosing, setChoosing] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [ads, setAds] = useState(false)

  useEffect(() => {
    startAnalytics()
    // Decided after mount so the pre-rendered HTML (no banner) matches the
    // first client render.
    if (!getConsent()) setOpen(true)

    const reopen = () => {
      const current = getConsent()
      setAnalytics(current?.analytics ?? false)
      setAds(current?.ads ?? false)
      setChoosing(true)
      setOpen(true)
    }
    window.addEventListener(OPEN_CONSENT_EVENT, reopen)
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen)
  }, [])

  if (!open) return null

  const decide = (choice: { analytics: boolean; ads: boolean }) => {
    setOpen(false)
    setChoosing(false)
    setConsent(choice)
  }

  const buttonClass =
    'rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-2xl rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl"
    >
      <h2 id="cookie-banner-title" className="text-base font-semibold text-gray-900">
        Cookies on CVCircuit
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">
        We use necessary cookies to keep you signed in. With your permission, we&apos;d also like to use
        analytics cookies to see how people use the site, and advertising cookies to measure which of our
        adverts work. We don&apos;t use them to show you adverts elsewhere.{' '}
        <Link href="/privacy#cookies" className="font-medium text-primary-600 hover:underline">
          Privacy policy
        </Link>
      </p>

      {choosing && (
        <div className="mt-4 space-y-3">
          <CookieToggle
            label="Analytics"
            description="Google Analytics: which pages people visit and which features they use."
            checked={analytics}
            onChange={setAnalytics}
          />
          <CookieToggle
            label="Advertising"
            description="Google Ads and Microsoft Advertising: whether a visit or sign-up came from one of our adverts."
            checked={ads}
            onChange={setAds}
          />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {choosing ? (
          <button type="button" onClick={() => decide({ analytics, ads })} className={`${buttonClass} bg-primary-600 text-white hover:bg-primary-700`}>
            Save choices
          </button>
        ) : (
          <>
            <button type="button" onClick={() => decide({ analytics: true, ads: true })} className={`${buttonClass} bg-primary-600 text-white hover:bg-primary-700`}>
              Accept all
            </button>
            <button type="button" onClick={() => decide({ analytics: false, ads: false })} className={`${buttonClass} bg-primary-600 text-white hover:bg-primary-700`}>
              Reject all
            </button>
            <button type="button" onClick={() => setChoosing(true)} className={`${buttonClass} text-primary-700 hover:bg-primary-50`}>
              Choose cookies
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function CookieToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span>
        <span className="block text-sm font-semibold text-gray-900">{label}</span>
        <span className="block text-xs text-gray-500">{description}</span>
      </span>
    </label>
  )
}

export function CookieSettingsLink({ className }: { className?: string }) {
  return (
    <button
      type="button"
      onClick={openConsentSettings}
      className={className ?? 'text-primary-600 font-semibold hover:underline'}
    >
      Cookie settings
    </button>
  )
}
